import { Collection } from "intake_shared/src/enum";
import { DepartmentServices, TRequestedService } from "intake_shared/src/types";
import db from "../../utils/firestore";
import { convertToSafeString } from "../../utils/utils";
import { v4 as uuidv4 } from "uuid";
import { fetchDepartmentAndServices, readDepartmentDocs } from "../department";

enum RequestedService {
    INDIVIDUAL_THERAPY = "Individual Therapy",
    COUPLES_THERAPY = "Couples Therapy",
    CHILD_THERAPY = "Child Therapy",
    FAMILY_THERAPY = "Family Therapy",
    GIFTED_ASSESSMENT = "Gifted Assessment",
    SHORT_TERM_EVALUATIONS_DOCUMENTATION_SERVICES = "Short Term Evaluations & Documentation Services",
    TELETHERAPY = "Teletherapy",
}

export enum Departments {
    HT = "HT",
    CHP = "CHP",
}

const defaultDepartments: DepartmentServices = {
    [Departments.CHP]: {
        practice_id: process.env.REACT_APP_MANTLE_CHP_PRACTICE_ID as string,
        label: "CHP",
        services: [
            "CAREER_ASSESSMENT",
            "EXTERN_THERAPY",
            "IN-PERSON_MED_MANAGEMENT",
            "IN-PERSON_PSYCH_COUNSELING",
            "KETAMINE_ASSISTED_PSYCHOTHERAPY",
            "KETAMINE_ASSISTED_PSYCHOTHERAPY_(EVALUATION)",
            "MEDICATION_MANAGEMENT",
            "MENTAL_HEALTH_ASSESSMENT:_FULLY_IN-PERSON_",
            "MENTAL_HEALTH_ASSESSMENT:_PARTIAL_IN-PERSON",
            "MENTAL_HEALTH_ASSESSMENT:_VIRTUAL",
            "PSYCHIATRIC_COUNSELING",
            "GIFTED_ASSESSMENT",
            "SHORT_TERM_EVALUATIONS_&_DOCUMENTATION_SERVICES",
        ],
    },
    [Departments.HT]: {
        practice_id: process.env.REACT_APP_MANTLE_PRACTICE_ID as string,
        label: "HT",
        services: [
            "CHILD_THERAPY",
            "COUPLES_THERAPY",
            "FAMILY_THERAPY",
            "IN-PERSON_THERAPY",
            "INDIVIDUAL_THERAPY",
            "INTUITIVE_EATING",
            "TELETHERAPY",
        ],
    },
};

interface Department {
    label?: string;
    fee_type?: string;
    practice_id?: string;
    services?: string[];
}
export type DepartmentCache = {
    [key: string]: Department;
};

let requestedServicesCache: TRequestedService = {};
let departmentsCache: DepartmentCache = {};
let deptAndServicesCache: DepartmentServices = {};

export const defaultRequestedServices: TRequestedService = RequestedService;

export async function addRequestedService(serviceLabel: string) {
    const serviceKey: string = convertToSafeString(serviceLabel, "_").toUpperCase();
    const safeServiceLabel: string = convertToSafeString(serviceLabel, " ");
    await db
        .collection(Collection.REQUESTED_SERVICES)
        .doc(serviceKey)
        .set({ label: safeServiceLabel });
}

// this had to change to assist adding the default departments and new department
export async function addNewDepartment(newLabel: string) {
    // for an entirely new department
    const newPracticeId = uuidv4();

    const deptKey = convertToSafeString(newLabel, "_").toUpperCase();
    const departmentData = {
        label: convertToSafeString(newLabel),
        practice_id: newPracticeId,
    };

    await db.collection(Collection.DEPARTMENTS).doc(deptKey).set(departmentData);
}

export async function removeRequestedService(serviceKey: string) {
    // Potentially unsafe to do this since we do not have a key reference.
    await db.collection(Collection.REQUESTED_SERVICES).doc(serviceKey).delete();
}

export async function updateRequestedServicesCache() {
    const requestedServicesDocs = (
        await db.collection(Collection.REQUESTED_SERVICES).get()
    ).docs;
    if (requestedServicesDocs.length) {
        requestedServicesCache = requestedServicesDocs.reduce((prev, curr) => {
            prev[curr.id] = curr.data().label;
            return prev;
        }, {} as { [key: string]: string });
        return requestedServicesCache;
    }
}

export async function updateDepartmentCache(): Promise<DepartmentCache> {
    try {
        const departmentsDocs = await readDepartmentDocs();

        if (departmentsDocs.length) {
            departmentsCache = departmentsDocs.reduce((cache, doc) => {
                const data = doc.data() as Department;
                cache[doc.id] = data;
                return cache;
            }, {} as DepartmentCache);
        }

        return departmentsCache;
    } catch (error) {
        console.error("Error fetching department data:", error);
        return {};
    }
}

export async function updateAllDepartmentAndServicesCache() {
    deptAndServicesCache = await fetchDepartmentAndServices();
    return deptAndServicesCache;
}

export async function getRequestedServices(): Promise<TRequestedService> {
    if (Object.keys(requestedServicesCache).length > 0) {
        return requestedServicesCache;
    }
    let requestedServices = await updateRequestedServicesCache();
    if (!requestedServices) {
        for (const serviceLabel of Object.values(RequestedService)) {
            try {
                // Write the default services if it does not exist at all in the store.
                await addRequestedService(serviceLabel);
            } catch (ex) {
                console.error("service cache exception", ex);
            }
        }
        requestedServices = await updateRequestedServicesCache();
    }

    return requestedServices || RequestedService;
}

export async function getDepartments(): Promise<DepartmentCache> {
    if (Object.keys(departmentsCache).length > 0) {
        return departmentsCache;
    }
    let departments = await updateDepartmentCache();
    return departments || defaultDepartments;
}

// function to delete a service
export async function deleteService(serviceKey: string) {
    try {
        await db.collection(Collection.REQUESTED_SERVICES).doc(serviceKey).delete();
    } catch (ex) {
        console.error("Error deleting service", ex);
        throw ex;
    }
}
